import {
  Col,
  Form,
  Modal,
  Row,
  SelectField,
  Space,
  SubmitButton,
  Text,
} from '@/components';
import { useForm, useInvalidateQuery } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useAddGroupTarget, useGroupTargets, useGroups } from '../_hooks/group';
import { AddGroupTargetFormSchema, type GroupModel } from '../_models/group';

type ModalProps = {
  open: boolean;
  objectId: string;
  onClose: () => void;
} & Omit<FormProps, 'onSubmit'>;

export const AddGroupTargetFormModal: FC<ModalProps> = ({
  open,
  objectId,
  targetType,
  fiscalYear,
  onClose,
}) => {
  const { t } = useTranslation();
  const { invalidate } = useInvalidateQuery();
  const { openMessage } = useMessage();
  const { mutateAsync } = useAddGroupTarget(objectId);
  return (
    <Modal
      open={open}
      title={t('add-to-project-group')}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
    >
      <Row gutter={[0, 8]}>
        <Col span={24}>
          <Space>
            <Text
              text={t('target-name', {
                name: targetType === 'project' ? t('project') : '',
              })}
            />
          </Space>
        </Col>
        <Col span={24}>
          <AddGroupTargetForm
            objectId={objectId}
            targetType={targetType}
            fiscalYear={fiscalYear}
            onSubmit={async (values) => {
              await mutateAsync(values);
              await Promise.all([
                invalidate(['group-targets']),
                openMessage('success', t('add-completed')),
                (async () => {
                  onClose();
                })(),
              ]);
            }}
          />
        </Col>
      </Row>
    </Modal>
  );
};

type FormValues = z.infer<typeof AddGroupTargetFormSchema>;

type FormProps = {
  objectId: string;
  targetType: z.infer<typeof GroupModel.shape.targetType>;
  fiscalYear: number | undefined;
  onSubmit: (values: FormValues) => void;
};

const AddGroupTargetForm: FC<FormProps> = ({
  objectId,
  targetType,
  fiscalYear,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const groups = useGroups({ targetType, fiscalYear });
  const groupTargets = useGroupTargets({ objectId });

  const currentTargetsInGroup = groups.data?.results.flatMap((group) => {
    return groupTargets.data?.groupWiseDetails.some(
      ({ groupId }) => groupId === group.id,
    )
      ? []
      : { value: group.id, label: group.name };
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(AddGroupTargetFormSchema),
  });

  return (
    <Form
      leavePageAlertCondition={null}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        name="groupId"
        label={t('project-group-name')}
        control={control}
        options={currentTargetsInGroup}
        errors={errors}
        allowClear={false}
      />

      <SubmitButton
        text={t('add')}
        loading={isSubmitting}
        disabled={!isDirty}
      />
    </Form>
  );
};
